/**
 * Osobiste ustawienia wąskiego paska ikon (tryb LISTA) — kolejność i widoczność KOLOROWYCH ikon
 * widoków menu. To warstwa WYGLĄDU nad menu z uprawnień: użytkownik może tylko przestawić albo
 * ukryć widok, który i tak widzi — nigdy nie odsłoni niczego ponad uprawnienia (lista wejściowa
 * jest już przefiltrowana przez menu per user/rola/zespół).
 *
 * Reguły nakładania:
 *  - kolejność = kody z `order`, które nadal istnieją; widoki spoza `order` (np. nowy moduł od
 *    admina) dochodzą NA KOŃCU w kolejności naturalnej — nic nie ginie przez stare ustawienia;
 *  - kod z ustawień, którego już nie ma na liście (uprawnienia zabrały moduł), jest ignorowany;
 *  - co najmniej jeden widok zostaje widoczny — gdy ustawienia ukrywają wszystko, pasek
 *    pokazuje pełną listę (bezpiecznik na wypadek np. zmiany uprawnień po zapisie).
 */

export interface RailPreferences {
  /** Kody widoków w kolejności wybranej przez użytkownika. */
  order: string[];
  /** Kody widoków ukrytych przez użytkownika. */
  hidden: string[];
  /** Ukryte SZARE skróty paska (stałe miejsca, tylko pokaż/ukryj) — klucze z `RAIL_TILE_KEYS`. */
  hiddenTiles: string[];
}

export const EMPTY_RAIL_PREFERENCES: RailPreferences = { order: [], hidden: [], hiddenTiles: [] };

/**
 * Szare skróty paska: górne (Dashboard / Kalendarz / Mapa obszarów) i dolna baza (Użytkownicy /
 * Firmy / Ustawienia). Mają stałe miejsca — użytkownik może je tylko ukryć. „Ustawienia" są
 * zablokowane: to jedyne wejście do tego konfiguratora, więc nie da się ich schować.
 */
export const RAIL_TOP_TILE_KEYS = ['dashboard', 'calendar', 'area-map'] as const;
export const RAIL_BASE_TILE_KEYS = ['users', 'companies', 'settings'] as const;
export type RailTileKey =
  | (typeof RAIL_TOP_TILE_KEYS)[number]
  | (typeof RAIL_BASE_TILE_KEYS)[number];
export const RAIL_LOCKED_TILE_KEYS: readonly RailTileKey[] = ['settings'];

export function isRailTileVisible(prefs: RailPreferences, key: RailTileKey): boolean {
  return RAIL_LOCKED_TILE_KEYS.includes(key) || !prefs.hiddenTiles.includes(key);
}

export interface RailEntry<T> {
  item: T;
  visible: boolean;
}

/** Wszystkie widoki w kolejności użytkownika, z flagą widoczności (dla ekranu ustawień). */
export function arrangeRailItems<T extends { code: string }>(
  items: T[],
  prefs: RailPreferences,
): RailEntry<T>[] {
  const byCode = new Map(items.map((it) => [it.code, it]));
  const hidden = new Set(prefs.hidden);
  const placed = new Set<string>();
  const ordered: T[] = [];
  for (const code of prefs.order) {
    const it = byCode.get(code);
    if (it && !placed.has(code)) {
      ordered.push(it);
      placed.add(code);
    }
  }
  for (const it of items) {
    if (!placed.has(it.code)) ordered.push(it);
  }
  return ordered.map((item) => ({ item, visible: !hidden.has(item.code) }));
}

/** Widoki do narysowania na pasku — kolejność użytkownika, bez ukrytych, nigdy pusto. */
export function visibleRailItems<T extends { code: string }>(
  items: T[],
  prefs: RailPreferences,
): T[] {
  const visible = arrangeRailItems(items, prefs)
    .filter((e) => e.visible)
    .map((e) => e.item);
  return visible.length > 0 ? visible : items;
}

/** Ustawienia do zapisu z bieżącej listy na ekranie. */
export function railPreferencesFromEntries<T extends { code: string }>(
  entries: RailEntry<T>[],
  hiddenTiles: readonly string[],
): RailPreferences {
  return {
    order: entries.map((e) => e.item.code),
    hidden: entries.filter((e) => !e.visible).map((e) => e.item.code),
    hiddenTiles: hiddenTiles.filter((k) => !RAIL_LOCKED_TILE_KEYS.includes(k as RailTileKey)),
  };
}

/** Czy dwie listy na ekranie są identyczne (kolejność + widoczność) — do „niezapisanych zmian". */
export function sameRailEntries<T extends { code: string }>(
  a: RailEntry<T>[],
  b: RailEntry<T>[],
): boolean {
  if (a.length !== b.length) return false;
  return a.every((e, i) => e.item.code === b[i]!.item.code && e.visible === b[i]!.visible);
}
